import { useEffect, useRef, useState } from "react"

/**
 * Mounts children only after the placeholder is near the viewport.
 * Once shown, stays mounted. `disabled` renders children immediately.
 */
export default function LazyMount({
  children,
  disabled = false,
  onVisible,
  rootMargin = "800px 0px",
  placeholderHeight = 400,
  className = "",
}) {
  const placeholderRef = useRef(null)
  const onVisibleRef = useRef(onVisible)
  onVisibleRef.current = onVisible
  const [visible, setVisible] = useState(Boolean(disabled))

  useEffect(() => {
    if (disabled) {
      setVisible(true)
      onVisibleRef.current?.()
    }
  }, [disabled])

  useEffect(() => {
    if (disabled || visible) return undefined
    const el = placeholderRef.current
    if (!el) return undefined

    if (typeof IntersectionObserver === "undefined") {
      setVisible(true)
      onVisibleRef.current?.()
      return undefined
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return
        onVisibleRef.current?.()
        setVisible(true)
        io.disconnect()
      },
      { root: null, rootMargin, threshold: 0 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [disabled, rootMargin, visible])

  if (disabled || visible) return children

  return (
    <div
      ref={placeholderRef}
      className={`dashboardLazyMountSlot ${className}`.trim()}
      style={{ minHeight: placeholderHeight }}
      aria-hidden
    />
  )
}
